/**
 * SOLID Review
 * S — Good, tighter than before: bundles generic app-shell state (toasts, modal stack, named
 *     loading flags) alongside game-specific transient view flags (isDiceRevealed/isPeekActive/
 *     isCupLifting/isRoundResultVisible — these describe gameplay presentation, not generic UI).
 *     The ad-refresh section (adRefreshIntervalSeconds/isAdContainerMounted/
 *     setAdRefreshInterval/mountAdContainer/unmountAdContainer) is deleted — it duplicated
 *     fields adStore already owns (refreshIntervalSeconds/isMounted, same action names) and had
 *     zero production consumers (confirmed via a repo-wide grep before deleting). Down to 3
 *     reasons to change, the game-view-flags vs. generic-UI tension being the remaining one.
 * O — Good. Each section (toasts/modals/loading/overlay) is additive independently — a new ModalId or
 *     LoadingKey is a union-member addition with no ripple.
 * L — N/A — no inheritance hierarchy.
 * I — Acceptable. 15 actions across 4 unrelated groupings; a component that only needs toasts still
 *     imports a type carrying modal/loading actions too.
 * D — Strong. Zero imports beyond zustand's `create` — no domain/infrastructure coupling.
 */
import { create } from 'zustand';

export type ToastSeverity = 'info' | 'success' | 'warning' | 'error';

export interface IEntityToast {
  readonly id: string;
  readonly message: string;
  readonly severity: ToastSeverity;
  readonly durationMs: number;
  readonly createdAt: Date;
}

export type ModalId =
  | 'CREATE_ROOM'
  | 'JOIN_ROOM'
  | 'RULES'
  | 'SETTINGS'
  | 'PURCHASE_CREDITS'
  | 'CONFIRM_LIFT'
  | 'GAME_OVER'
  | 'ROUND_RESULT'
  | 'PLAYER_ELIMINATED'
  | 'LEAVE_ROOM_CONFIRM'
  | 'NETWORK_ERROR';

export interface IStateModal {
  readonly id: ModalId;
  readonly props: Record<string, unknown>;
}

export type LoadingKey =
  | 'CREATE_ROOM'
  | 'JOIN_ROOM'
  | 'START_GAME'
  | 'ROLL_DICE'
  | 'MAKE_CLAIM'
  | 'MAKE_DECISION'
  | 'PURCHASE_CREDITS'
  | 'AUTHENTICATE'
  | 'LIVEKIT_CONNECT'
  | 'FETCH_ROOM'
  | 'SUBMIT_CLAIM';

export interface IStateUI {
  // ── IEntityToast notifications ────────────────────────────────────────────────────
  readonly toasts: ReadonlyArray<IEntityToast>;

  // ── Modal stack ────────────────────────────────────────────────────────────
  readonly activeModal: IStateModal | null;

  // ── Loading flags ──────────────────────────────────────────────────────────
  readonly loadingFlags: ReadonlyMap<LoadingKey, boolean>;

  // ── Overlay / transient view flags ────────────────────────────────────────
  readonly isDiceRevealed: boolean;
  readonly isPeekActive: boolean;
  readonly isCupLifting: boolean;
  readonly isRoundResultVisible: boolean;
  readonly isGameOverVisible: boolean;
  readonly isSidebarOpen: boolean;
  readonly isConnectionBannerVisible: boolean;
  readonly connectionBannerMessage: string | null;
}

export interface IStateUIActions {
  // ── IEntityToast actions ──────────────────────────────────────────────────────────
  pushToast: (
    message: string,
    severity?: ToastSeverity,
    durationMs?: number,
  ) => void;
  dismissToast: (id: string) => void;
  clearAllToasts: () => void;

  // ── Modal actions ──────────────────────────────────────────────────────────
  openModal: (id: ModalId, props?: Record<string, unknown>) => void;
  closeModal: () => void;

  // ── Loading flag actions ───────────────────────────────────────────────────
  setLoading: (key: LoadingKey, value: boolean) => void;
  isLoading: (key: LoadingKey) => boolean;

  // ── Overlay / view flag actions ────────────────────────────────────────────
  setDiceRevealed: (value: boolean) => void;
  setPeekActive: (value: boolean) => void;
  setCupLifting: (value: boolean) => void;
  setRoundResultVisible: (value: boolean) => void;
  setGameOverVisible: (value: boolean) => void;
  toggleSidebar: () => void;
  setSidebarOpen: (value: boolean) => void;
  setConnectionBanner: (message: string | null) => void;

  // ── Batch reset ────────────────────────────────────────────────────────────
  resetTransientState: () => void;
}

export type UIStore = IStateUI & IStateUIActions;

const INITIAL_STATE: IStateUI = {
  toasts: [],
  activeModal: null,
  loadingFlags: new Map<LoadingKey, boolean>(),
  isDiceRevealed: false,
  isPeekActive: false,
  isCupLifting: false,
  isRoundResultVisible: false,
  isGameOverVisible: false,
  isSidebarOpen: false,
  isConnectionBannerVisible: false,
  connectionBannerMessage: null,
};

let toastCounter = 0;

const generateToastId = (): string => {
  toastCounter += 1;
  return `toast-${Date.now()}-${toastCounter}`;
};

export const useUIStore = create<UIStore>((set, get) => ({
  ...INITIAL_STATE,

  // ── IEntityToast actions ────────────────────────────────────────────────────────

  pushToast: (
    message: string,
    severity: ToastSeverity = 'info',
    durationMs = 4000,
  ): void => {
    const toast: IEntityToast = {
      id: generateToastId(),
      message,
      severity,
      durationMs,
      createdAt: new Date(),
    };
    set((state) => ({
      toasts: [...state.toasts, toast],
    }));
    if (durationMs > 0) {
      setTimeout(() => {
        get().dismissToast(toast.id);
      }, durationMs);
    }
  },

  dismissToast: (id: string): void => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  clearAllToasts: (): void => {
    set({ toasts: [] });
  },

  // ── Modal actions ────────────────────────────────────────────────────────

  openModal: (id: ModalId, props: Record<string, unknown> = {}): void => {
    set({ activeModal: { id, props } });
  },

  closeModal: (): void => {
    set({ activeModal: null });
  },

  // ── Loading flag actions ─────────────────────────────────────────────────

  setLoading: (key: LoadingKey, value: boolean): void => {
    set((state) => {
      const next = new Map(state.loadingFlags);
      if (value) {
        next.set(key, true);
      } else {
        next.delete(key);
      }
      return { loadingFlags: next };
    });
  },

  isLoading: (key: LoadingKey): boolean => {
    return get().loadingFlags.get(key) === true;
  },

  // ── Overlay / view flag actions ──────────────────────────────────────────

  setDiceRevealed: (value: boolean): void => {
    set({ isDiceRevealed: value });
  },

  setPeekActive: (value: boolean): void => {
    set({ isPeekActive: value });
  },

  setCupLifting: (value: boolean): void => {
    set({ isCupLifting: value });
  },

  setRoundResultVisible: (value: boolean): void => {
    set({ isRoundResultVisible: value });
  },

  setGameOverVisible: (value: boolean): void => {
    set({ isGameOverVisible: value });
  },

  toggleSidebar: (): void => {
    set((state) => ({ isSidebarOpen: !state.isSidebarOpen }));
  },

  setSidebarOpen: (value: boolean): void => {
    set({ isSidebarOpen: value });
  },

  setConnectionBanner: (message: string | null): void => {
    set({
      connectionBannerMessage: message,
      isConnectionBannerVisible: message !== null,
    });
  },

  // ── Batch reset ──────────────────────────────────────────────────────────

  resetTransientState: (): void => {
    set({
      isDiceRevealed: false,
      isPeekActive: false,
      isCupLifting: false,
      isRoundResultVisible: false,
      isGameOverVisible: false,
      isSidebarOpen: false,
      isConnectionBannerVisible: false,
      connectionBannerMessage: null,
      activeModal: null,
      toasts: [],
      loadingFlags: new Map<LoadingKey, boolean>(),
    });
  },
}));